import { uniqBy } from "@s-libs/micro-dash";
import { NextRouter } from "next/router";

import contentTypes, { type ContentType } from "@/view/constants/contentTypes";
import { getPhetTeacherTipsPdfUrl } from "@/view/UnlevelledContent/UnlevelledContentActivityPanel/phetMockData";
import { formatGeniusActivityPanelContent } from "@/view/UnlevelledContent/utils";

import type { GenerationGeniusAPIResponse } from "@/types/models/GenerationGenius.interface";
import type {
  GeniusAttachment,
  UnlevelledContentPDF,
} from "@/types/models/UnlevelledContent.interface";
import type {
  TGeniusPanelName,
  TGeniusPanelOptionType,
} from "@/view/UnlevelledContent/UnlevelledContentActivityPanel/UnlevelledContentActivityPanel.constant";

export const getClickHandler = ({
  router,
  contentData,
}: {
  router: NextRouter;
  contentData: GenerationGeniusAPIResponse;
}) => {
  return ({
    type,
    activityPanel,
    url,
    assignmentId,
  }: {
    type: TGeniusPanelOptionType;
    activityPanel?: TGeniusPanelName;
    assignmentId?: string | undefined;
    url?: string | undefined;
  }) => {
    if (type === "download") {
      downloadGeniusPDF({ contentData, activityPanel });
    } else if (type === "external-link" && url) {
      window.open(url, "_blank");
    } else if (activityPanel) {
      openUnlevelledContentActivityPanel({
        router,
        activityPanel,
        assignmentId,
      });
    }
  };
};

export const openUnlevelledContentActivityPanel = ({
  router,
  activityPanel,
  assignmentId,
}: {
  router: NextRouter;
  activityPanel: TGeniusPanelName;
  assignmentId?: string | undefined;
}) => {
  const href = {
    pathname: router.pathname,
    query: {
      ...router.query,
      activityPanel: activityPanel.toLowerCase(),
      ...(assignmentId && { assignmentId }),
    },
  };
  router.replace(href, undefined, { shallow: true });
};

export const downloadGeniusPDF = ({
  contentData,
  activityPanel,
}: {
  contentData: GenerationGeniusAPIResponse;
  activityPanel: TGeniusPanelName | undefined;
}) => {
  try {
    const activityType = activityPanel
      ?.toLowerCase()
      .replaceAll(" ", "_")
      .replace(/\(PDF\)/g, "")
      .trim();

    const pdfs = JSON.parse(contentData.pdfs) as UnlevelledContentPDF[];
    const pdfData = pdfs.find((pdf) => pdf.type === activityType);
    const fileURL = pdfData?.url;

    // Create a link element to trigger the download
    if (fileURL) {
      const link = document.createElement("a");
      link.href = fileURL;
      link.download = `${pdfData.type}.pdf`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    console.error("Error downloading lesson plan PDF:", error);
    throw error;
  }
};

export const getBackButtonHandler = (router: NextRouter) => {
  return () => {
    const query = { ...router.query };
    delete query?.["activityPanel"];
    delete query?.["assignmentId"];
    void router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  };
};

export const getPdfResourceVisibility = (
  pdfResources: UnlevelledContentPDF[],
) => {
  const resourceSet = new Set(pdfResources.map((resource) => resource.type));

  return {
    hasReadingMaterial: resourceSet.has("reading_materials"),
    hasDiyPdfUrl: resourceSet.has("diy_activity_guide"),
    hasTeacherResources:
      resourceSet.has("teacher_guide") || resourceSet.has("lesson_plan"),
  };
};

export const getActivitiesFromContentData = ({
  contentData,
  isTeacher,
  contentType,
  contentId,
}: {
  contentData: GenerationGeniusAPIResponse;
  isTeacher: boolean;
  contentType?: ContentType;
  contentId?: string;
}) => {
  const { vocabulary, discussionQuestions, pdfResources } =
    formatGeniusActivityPanelContent(contentData);

  const hasVocabulary = Boolean(vocabulary?.content?.length);

  const hasDiscussionQuestions = Boolean(discussionQuestions?.content?.length);

  const { hasDiyPdfUrl, hasTeacherResources, hasReadingMaterial } =
    getPdfResourceVisibility(pdfResources);

  const isPhetSimulation = contentType === contentTypes.PHET_SIMULATION;
  const phetTeacherTipsPdfUrl =
    isPhetSimulation && contentId ? getPhetTeacherTipsPdfUrl(contentId) : null;

  const activities: {
    title: string;
    hidden: boolean;
    type: TGeniusPanelOptionType;
    activityPanel: TGeniusPanelName;
    url?: string | undefined;
  }[] = [
    {
      title: "Teacher resources",
      hidden: !hasTeacherResources || !isTeacher,
      type: "internal-panel-link",
      activityPanel: "teacher_resources",
    },
    {
      title: "Teacher Tips (PDF)",
      hidden: !phetTeacherTipsPdfUrl || !isTeacher,
      type: "external-link",
      activityPanel: "teacher_tips",
      url: phetTeacherTipsPdfUrl ?? undefined,
    },
    {
      title: "Reading material (PDF)",
      hidden: !hasReadingMaterial,
      type: "external-link",
      activityPanel: "reading_materials",
      url: pdfResources.find((pdf) => pdf.type === "reading_materials")?.url,
    },
    {
      title: "Discussion questions",
      hidden: !hasDiscussionQuestions,
      type: "internal-panel-link",
      activityPanel: "discussion_questions",
    },
    {
      title: "Vocabulary",
      hidden: !hasVocabulary,
      type: "internal-panel-link",
      activityPanel: "vocabulary",
    },
    {
      title: "DIY activity guide (PDF)",
      hidden: !hasDiyPdfUrl,
      type: "external-link",
      activityPanel: "diy_activity_guide",
      url: pdfResources.find((pdf) => pdf.type === "diy_activity_guide")?.url,
    },
  ];

  return activities.filter((activity) => !activity.hidden);
};

export const getSortedFormativeAssessments = ({
  assessments,
  expectedNames,
}: {
  assessments: GeniusAttachment[];
  expectedNames: string[];
}) => {
  if (!assessments || assessments.length === 0) return [];

  const uniqueAssessments = uniqBy(
    assessments,
    (attachment) => attachment.data.name,
  );

  const attachmentMap = new Map(assessments.map((att) => [att.data.name, att]));

  // Assessments in expected order with undefined values filtered out
  const orderedAssessments = expectedNames
    .map((name) => attachmentMap.get(name))
    .filter((att): att is (typeof uniqueAssessments)[0] => att !== undefined);

  // Assessments not found in expectedNames are placed at the end
  const additionalAssessments = uniqueAssessments.filter(
    (att) => !expectedNames.includes(att.data.name),
  );

  return [...orderedAssessments, ...additionalAssessments];
};
